/**
 * Pure helpers for the Products section and the product quality drawer: the latest phase of a
 * product, the reason behind its verdict in one sentence, and its failed rules most serious first.
 */
import type {
  CheckResult,
  PhaseScore,
  ProductScore,
  ProductScoreDetail,
} from '../types/quality-scores.types';

import { fieldRank } from './field-priority';
import { fieldLabel, formatScore, PASS_GATE, REVIEW_GATE, ruleLabel } from './quality-labels';

/** One product row reduced to the job's latest phase (after when the job ran it, else before). */
export interface LatestProductScore {
  score: number | null;
  verdict: string;
  problems: number;
}

/**
 * A list row's score, verdict and failed-check count in the JOB's latest phase. The phase is
 * picked per job (`showPost`), never per row, so the scatter, table, family counts and KPIs agree.
 */
export function latestOfRow(row: ProductScore, showPost: boolean): LatestProductScore {
  return showPost
    ? { score: row.post, verdict: row.postVerdict, problems: row.failedChecksPost }
    : { score: row.pre, verdict: row.preVerdict, problems: row.failedChecksPre };
}

/**
 * The short "why" under a list row's verdict. The list has no severities, so a Fail at 70+
 * names both causes the contract allows: a critical problem or one field under 70.
 */
export function rowVerdictReason(verdict: string, score: number | null): string {
  if (verdict === 'FAIL') {
    if (score !== null && score >= REVIEW_GATE) return 'Has a critical problem or a field under 70';
    return 'Below 70';
  }
  if (verdict === 'REVIEW') {
    if (score !== null && score < PASS_GATE) {
      return `${formatScore(PASS_GATE - score)} points from Pass`;
    }
    return 'A field scores under 90';
  }
  if (verdict === 'PASS') return 'Passes';
  return 'Not scored';
}

/** The phase that stands for a product: after enrichment when it ran, else before. */
export function latestPhase(detail: ProductScoreDetail): PhaseScore | null {
  return detail.post ?? detail.pre;
}

/** A field's place in the app-wide priority order (title first); see `field-priority.ts`. */
export { fieldRank };

const SEVERITY_RANK: Record<string, number> = { CRITICAL: 0, MAJOR: 1, MINOR: 2 };

/** One failed rule on one field in the latest phase. */
export interface FailedCheck {
  field: string;
  check: CheckResult;
}

/** Every failed rule in a phase, Critical → Major → Minor, then in canonical field order. */
export function failedChecks(phase: PhaseScore | null): FailedCheck[] {
  const rows: FailedCheck[] = [];
  for (const f of phase?.fields ?? []) {
    for (const check of f.checks) if (!check.passed) rows.push({ field: f.field, check });
  }
  return rows.sort(
    (a, b) =>
      (SEVERITY_RANK[a.check.severity] ?? 3) - (SEVERITY_RANK[b.check.severity] ?? 3) ||
      fieldRank(a.field) - fieldRank(b.field),
  );
}

/**
 * The rule's threshold in words. The service tags optional fields "(optional)" even where the
 * product's category makes them required; a check that FAILED was required here, so drop it.
 */
function needsText(check: CheckResult): string {
  return check.passed ? check.threshold : check.threshold.replace(/\s*\(optional\)\s*$/i, '');
}

/** "Found 234 chars · needs 500 or more", leaving out whichever half is empty; null when both are. */
export function checkDetail(check: CheckResult): string | null {
  const found = check.measured !== '' ? check.measured : check.message;
  const parts = [
    ...(found !== '' ? [`Found ${found}`] : []),
    ...(check.threshold !== '' ? [`needs ${needsText(check)}`] : []),
  ];
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** The field with the lowest score in a phase, or null when none is scored. */
function worstField(phase: PhaseScore): { field: string; score: number } | null {
  let worst: { field: string; score: number } | null = null;
  for (const f of phase.fields) {
    if (f.score !== null && (worst === null || f.score < worst.score)) {
      worst = { field: f.field, score: f.score };
    }
  }
  return worst;
}

/**
 * The verdict in one sentence, from the latest phase's own data: a critical problem first (it
 * fails at any score), then the overall band, then a field under the gate that pulls it down.
 */
export function verdictSentence(phase: PhaseScore): string {
  const overall = phase.overall;
  const worst = worstField(phase);
  if (phase.verdict === 'FAIL') {
    const critical = failedChecks(phase).find((row) => row.check.severity === 'CRITICAL');
    if (critical) {
      return `Fails because ${ruleLabel(critical.check.checkId, critical.field)} — a critical problem fails a product at any score.`;
    }
    if (overall !== null && overall < REVIEW_GATE) return 'Fails because it scores below 70.';
    if (worst && worst.score < REVIEW_GATE) {
      return `Fails because ${fieldLabel(worst.field)} scores ${formatScore(worst.score)} — any field below 70 fails the product.`;
    }
    return "Fails this channel's rules.";
  }
  if (phase.verdict === 'REVIEW') {
    if (overall !== null && overall < PASS_GATE) {
      return `Needs review — ${formatScore(PASS_GATE - overall)} points from Pass.`;
    }
    if (worst && worst.score < PASS_GATE) {
      return `Needs review because ${fieldLabel(worst.field)} scores ${formatScore(worst.score)}.`;
    }
    return 'Needs review.';
  }
  if (phase.verdict === 'PASS') return 'Passes — 90 or more with no critical problems.';
  return 'This product was not given a verdict.';
}

/**
 * Why a verdict is worse than its score's zone, for the blocked pill's tooltip: names the critical
 * check (or weakest field) when the phase is known — scoped to a business view when `lens` is given.
 */
export function blockedVerdictReason(
  verdict: string,
  phase?: PhaseScore | null,
  lens?: string | null,
): string {
  const inLens = (check: CheckResult) =>
    !lens || check.lenses.length === 0 || check.lenses.includes(lens);
  // A field's score in the view being read: its lens score when scoped, else its overall score.
  const fieldScore = (f: PhaseScore['fields'][number]) =>
    lens ? (f.lenses[lens] ?? f.score) : f.score;
  let worst: { field: string; score: number } | null = null;
  for (const f of phase?.fields ?? []) {
    const score = fieldScore(f);
    if (score !== null && (worst === null || score < worst.score))
      worst = { field: f.field, score };
  }
  if (verdict === 'FAIL') {
    const critical = failedChecks(phase ?? null).find(
      (row) => row.check.severity === 'CRITICAL' && inLens(row.check),
    );
    if (critical) {
      return `A critical problem (${ruleLabel(critical.check.checkId, critical.field)}) fails this product at any score.`;
    }
    if (worst && worst.score < REVIEW_GATE) {
      return `${fieldLabel(worst.field)} scores ${formatScore(worst.score)} — any field below 70 fails this product at any score.`;
    }
    return 'A critical problem or a field under 70 fails this product at any score.';
  }
  if (worst && worst.score < PASS_GATE) {
    return `${fieldLabel(worst.field)} scores ${formatScore(worst.score)} — any field under 90 holds this product at Needs review at any score.`;
  }
  return 'A field under 90 holds this product at Needs review at any score.';
}

/** Failed rules per severity ("1 critical · 2 major · 7 minor"), most serious first; zero counts dropped. */
export function severityCounts(rows: FailedCheck[]): { severity: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const row of rows) counts.set(row.check.severity, (counts.get(row.check.severity) ?? 0) + 1);
  return [...counts.entries()]
    .map(([severity, count]) => ({ severity, count }))
    .sort((a, b) => (SEVERITY_RANK[a.severity] ?? 3) - (SEVERITY_RANK[b.severity] ?? 3));
}

/**
 * The "fix it and it stops failing" hint, only when the data proves it: a Fail at 70+ whose
 * only causes are critical rules (no field under 70). Fixing a critical rule removes its
 * cost, so the score can only rise — the product would at least need review. Null otherwise.
 */
export function criticalFixHint(phase: PhaseScore): string | null {
  const overall = phase.overall;
  if (phase.verdict !== 'FAIL' || overall === null || overall < REVIEW_GATE) return null;
  const criticals = failedChecks(phase).filter((row) => row.check.severity === 'CRITICAL');
  if (criticals.length === 0) return null;
  // Any field under 70 — even the critical one, whose other rules may keep it there — could
  // still fail the product after the fix, so the claim is only proven when none is.
  const anyLow = phase.fields.some((f) => f.score !== null && f.score < REVIEW_GATE);
  if (anyLow) return null;
  const subject =
    criticals.length === 1 ? 'it' : `these ${criticals.length.toLocaleString()} critical problems`;
  return `Fix ${subject} and this product would need review at worst instead (${formatScore(overall)} is above 70).`;
}

/** The product's title for the drawer header: the AI's title when written, else the feed's. */
export function productTitle(detail: ProductScoreDetail): string | null {
  const title = detail.content.find((c) => c.field === 'TITLE');
  if (!title) return null;
  if (detail.post && title.after) return title.after;
  if (!title.before) return null;
  return title.before;
}
